import type { RouteObject } from "react-router";

import { AppLayout } from "@/src/components/layout/AppLayout";
import { AccountDetail } from "@/src/pages/AccountDetail/AccountDetail";
import { Accounts } from "@/src/pages/Accounts/Accounts";
import { Dashboard } from "@/src/pages/Dashboard/Dashboard";
import { EventDetail } from "@/src/pages/EventDetail/EventDetail";
import { Events } from "@/src/pages/Events/Events";
import { Placeholder } from "@/src/pages/Placeholder/Placeholder";
import { PostDetail } from "@/src/pages/PostDetail/PostDetail";
import { Posts } from "@/src/pages/Posts/Posts";
import { Reports } from "@/src/pages/Reports/Reports";
import { SignIn } from "@/src/pages/SignIn/SignIn";
import { AriaRouting } from "@/src/routes/AriaRouting";
import { PublicOnly, RequireAuth } from "@/src/routes/guards";
import { NotFoundPage } from "@/src/routes/NotFoundPage";
import { ROUTES } from "@/src/routes/paths";

/**
 * Arbre des routes.
 *
 * Décrit à part du routeur lui-même pour que les tests puissent le monter dans
 * un `createMemoryRouter` — c'est ce qui permet de vérifier les gardes sans
 * navigateur ni historique réel.
 *
 * Quatre niveaux, dans cet ordre :
 *
 *  1. `AriaRouting` — branche les liens HeroUI sur le routeur ;
 *  2. `PublicOnly` / `RequireAuth` — routes de mise en page **sans chemin**,
 *     qui portent la garde ;
 *  3. `AppLayout` — la coque (barre latérale, en-tête), elle aussi sans chemin ;
 *  4. les écrans.
 *
 * ⚠️ **Un nouvel écran privé se déclare dans les enfants d'`AppLayout`**,
 * jamais à la racine. Une route ajoutée au mauvais niveau est publique sans que
 * rien ne le signale — et si elle est au bon niveau mais hors de la coque, elle
 * s'affiche sans navigation, ce qui se voit tout de suite.
 *
 * ⚠️ La connexion est **hors de la coque** : pas de barre latérale sur un
 * écran où il n'y a rien à naviguer, et elle a son propre panneau de marque.
 */
export const appRoutes: RouteObject[] = [
  {
    element: <AriaRouting />,
    children: [
      {
        element: <PublicOnly />,
        children: [{ path: ROUTES.signIn, element: <SignIn /> }],
      },
      {
        element: <RequireAuth />,
        children: [
          {
            element: <AppLayout />,
            children: [
              { index: true, element: <Dashboard /> },
              { path: ROUTES.reports, element: <Reports /> },
              { path: ROUTES.posts, element: <Posts /> },
              { path: ROUTES.postPattern, element: <PostDetail /> },
              { path: ROUTES.events, element: <Events /> },
              { path: ROUTES.eventPattern, element: <EventDetail /> },
              { path: ROUTES.accounts, element: <Accounts /> },
              { path: ROUTES.accountPattern, element: <AccountDetail /> },
              {
                path: ROUTES.settings,
                element: <Placeholder section="settings" />,
              },
            ],
          },
        ],
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];
